import {InputAdornment} from '@mui/material'
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded'
import {z} from 'zod'
import ZodConfig from 'constants/zodConfig'
import providerName from 'constants/enum/providerName'

const normalizeConfigKey = (value) => (value || '').trim()

const name = {
    name: 'name',
    rules: {required: true},
    sm: 6,
    xs: 12,
    InputProps: {
        startAdornment: (
            <InputAdornment position="start">
                <PeopleAltRoundedIcon />
            </InputAdornment>
        ),
    },
}
const location = {
    name: 'location',
    rules: {required: true},
    sm: 6,
    xs: 12,
}
const dayOfMonth = {
    name: 'dayOfMonth',
    type: 'number',
    rules: {required: true},
    sm: 12,
    xs: 12,
}

const serviceRate = {
    label: 'serviceRate',
    name: 'rates.serviceRate',
    rules: {required: true},
    sm: 6,
    xs: 12,
}

const terminalName = {
    label: 'terminalName',
    name: 'payment.terminalName',
    rules: {required: false},
    sm: 6,
    xs: 12,
}

const paymentProvider = {
    label: 'paymentProvider',
    name: 'payment.provider',
    type: 'select',
    options: providerName.list,
    rules: {required: false},
    sm: 6,
    xs: 12,
}

const configKey = {
    label: 'key',
    name: 'key',
    rules: {required: true},
    sm: 6,
    xs: 12,
    inputProps: {maxLength: 100},
}

const configValue = {
    label: 'value',
    name: 'value',
    type: 'password',
    rules: {required: true},
    sm: 6,
    xs: 12,
}

const isTwoFactorAuth = {
    label: 'isTwoFactorAuth',
    name: 'isTwoFactorAuth',
    type: 'switch',
    rules: {required: false},
    sm: 12,
    xs: 12,
}

const configs = {
    title: 'configs',
    type: 'array',
    withButtons: true,
    appendOnEmpty: false,
    arrayName: 'configs',
    items: {key: configKey, value: configValue},
}

export const createAccountField = {
    createAccount: {
        title: 'createAccount',
        items: {name, location, dayOfMonth},
    },
    rates: {
        title: 'rates',
        items: {serviceRate},
    },
    payment: {
        title: 'payment',
        items: {paymentProvider, terminalName},
    },
    configs,
}

export const editAccountField = {
    editAccount: {
        title: 'editAccount',
        items: {name, location, dayOfMonth},
    },
    security: {
        title: 'security',
        items: {isTwoFactorAuth},
    },
    rates: {
        title: 'rates',
        items: {serviceRate},
    },
    payment: {
        title: 'payment',
        items: {paymentProvider, terminalName},
    },
    configs,
}

export const accountFieldValidation = z
    .object({
        name: ZodConfig.string(),
        location: ZodConfig.string(),
        dayOfMonth: ZodConfig.number({min: 1, max: 31}),
        rates: ZodConfig.object({
            serviceRate: ZodConfig.number({min: 0, max: 100}),
        }),
        payment: ZodConfig.object({
            provider: ZodConfig.enum(providerName.list).optional(),
            terminalName: ZodConfig.string().optional(),
        }),
        isTwoFactorAuth: ZodConfig.boolean().optional(),
        configs: ZodConfig.array(
            z.object({
                key: z
                    .string({message: 'required'})
                    .trim()
                    .min(1, {message: 'required'})
                    .max(100, {message: 'config_key_max'}),
                value: z.string({message: 'required'}).min(1, {message: 'required'}),
            })
        )
            .transform((items) =>
                items?.map((item) => ({
                    ...item,
                    key: normalizeConfigKey(item.key),
                }))
            )
            .optional(),
    })
    .superRefine((data, ctx) => {
        const keys = new Set()
        data.configs?.forEach((config, index) => {
            if (!config?.key) return
            const normalizedKey = normalizeConfigKey(config.key).toLowerCase()
            if (keys.has(normalizedKey)) {
                ctx.addIssue({
                    path: ['configs', index, 'key'],
                    code: z.ZodIssueCode.custom,
                    message: 'duplicate_config_key',
                })
            } else {
                keys.add(normalizedKey)
            }
        })
    })
